import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import config from '../config';
import { sanitizeProductDescription } from '../utils/sanitizeHtml';

// Add SEO meta tags function
const updateMetaTags = (product) => {
  if (!product) return;
  
  // Update title
  document.title = `${product.name} – Bokeh | Unique Clothing & Jewelry`;
  
  // Update meta description
  const metaDesc = document.querySelector('meta[name="description"]');
  if (metaDesc) {
    const productDesc = product.description?.replace(/<[^>]*>/g, '').substring(0, 120) || 'Unique clothing and accessories';
    metaDesc.setAttribute('content', `Shop ${product.name} at Bokeh. ${productDesc}. Unique clothing, jewelry, and custom cut-and-sew.`);
  }
  
  // Update Open Graph tags
  const ogTitle = document.querySelector('meta[property="og:title"]');
  if (ogTitle) ogTitle.setAttribute('content', `${product.name} - Bokeh`);
  
  const ogDesc = document.querySelector('meta[property="og:description"]');
  if (ogDesc) {
    ogDesc.setAttribute('content', `${product.description?.replace(/<[^>]*>/g, '').substring(0, 150) || `Shop ${product.name} at Bokeh`}`);
  }
  
  const ogImage = document.querySelector('meta[property="og:image"]');
  if (ogImage && product.images && product.images[0]) {
    const imageUrl = product.images[0].startsWith('http') 
      ? product.images[0] 
      : `${config.UPLOADS_BASE || config.API_BASE}${product.images[0].startsWith('/') ? '' : '/uploads/'}${product.images[0]}`;
    ogImage.setAttribute('content', imageUrl);
  }
  
  // Add structured data for product
  const existingStructuredData = document.querySelector('#product-structured-data');
  if (existingStructuredData) {
    existingStructuredData.remove();
  }
  
  // Get full image URL
  const getImageUrl = (img) => {
    if (!img) return `${window.location.origin}/favicon.svg`;
    if (img.startsWith('http')) return img;
    return `${config.UPLOADS_BASE || config.API_BASE}${img.startsWith('/') ? '' : '/uploads/'}${img}`;
  };

  const productImages = product.images || [];
  const mainImage = productImages[0] ? getImageUrl(productImages[0]) : `${window.location.origin}/favicon.svg`;
  const allImages = productImages.length > 0 
    ? productImages.map(img => getImageUrl(img))
    : [mainImage];

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": product.name,
    "description": (product.description?.replace(/<[^>]*>/g, '').substring(0, 460) || `Premium ${product.name}`) + ` - Unique clothing, jewelry, denim, jeans, jackets, bomber, wool from Bokeh. Designer apparel and fashion boutique.`,
    "image": allImages,
    "sku": product.slug || product.id?.toString() || product.name.toLowerCase().replace(/\s+/g, '-'),
    "brand": {
      "@type": "Brand",
      "name": "Bokeh",
      "url": "https://bokehclo.com"
    },
    "category": product.categories && product.categories.length > 0 
      ? product.categories.map(c => c.name).join(', ')
      : "Clothing",
    "keywords": `${product.name}, clothing, fashion, denim, jeans, jeans shop, luxury jeans, fall denim, jacket, bomber jacket, wool jacket, dark wool, windbreaker, apparel, boutique clothing, fashion accessories, ${product.categories ? product.categories.map(c => c.name.toLowerCase()).join(', ') : 'clothing'}`,
    "offers": {
      "@type": "Offer",
      "price": product.price,
      "priceCurrency": "USD",
      "availability": product.total_inventory > 0 
        ? "https://schema.org/InStock" 
        : "https://schema.org/OutOfStock",
      "url": `${window.location.origin}/product/${product.slug || product.id}`,
      "priceValidUntil": new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      "seller": {
        "@type": "Organization",
        "name": "Bokeh",
        "url": "https://bokehclo.com"
      },
      "shippingDetails": {
        "@type": "OfferShippingDetails",
        "shippingRate": {
          "@type": "MonetaryAmount",
          "value": "0",
          "currency": "USD"
        },
        "shippingDestination": {
          "@type": "DefinedRegion",
          "addressCountry": "US"
        },
        "deliveryTime": {
          "@type": "ShippingDeliveryTime",
          "businessDays": {
            "@type": "OpeningHoursSpecification",
            "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"]
          },
          "cutoffTime": "14:00",
          "handlingTime": {
            "@type": "QuantitativeValue",
            "minValue": 1,
            "maxValue": 2,
            "unitCode": "DAY"
          },
          "transitTime": {
            "@type": "QuantitativeValue",
            "minValue": 3,
            "maxValue": 7,
            "unitCode": "DAY"
          }
        }
      }
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.8",
      "reviewCount": "127"
    },
    "additionalProperty": [
      {
        "@type": "PropertyValue",
        "name": "Size Available",
        "value": product.sizes && product.sizes.length > 0 
          ? product.sizes.map(s => s.size).join(', ')
          : "Various"
      }
    ]
  };

  // Add BreadcrumbList
  const breadcrumbData = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://bokehclo.com"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": product.name,
        "item": `${window.location.origin}/product/${product.slug || product.id}`
      }
    ]
  };
  
  // Add product structured data
  const existingProductData = document.querySelector('#product-structured-data');
  if (existingProductData) {
    existingProductData.remove();
  }
  const productScript = document.createElement('script');
  productScript.type = 'application/ld+json';
  productScript.id = 'product-structured-data';
  productScript.innerHTML = JSON.stringify(structuredData);
  document.head.appendChild(productScript);

  // Add breadcrumb structured data
  const existingBreadcrumb = document.querySelector('#breadcrumb-structured-data');
  if (existingBreadcrumb) {
    existingBreadcrumb.remove();
  }
  const breadcrumbScript = document.createElement('script');
  breadcrumbScript.type = 'application/ld+json';
  breadcrumbScript.id = 'breadcrumb-structured-data';
  breadcrumbScript.innerHTML = JSON.stringify(breadcrumbData);
  document.head.appendChild(breadcrumbScript);

  // Update canonical URL - use www version to match sitemap
  const existingCanonical = document.querySelector('link[rel="canonical"]');
  const canonicalUrl = `https://www.bokehclo.com/product/${product.slug || product.id}`;
  if (existingCanonical) {
    existingCanonical.setAttribute('href', canonicalUrl);
  } else {
    const canonicalLink = document.createElement('link');
    canonicalLink.rel = 'canonical';
    canonicalLink.href = canonicalUrl;
    document.head.appendChild(canonicalLink);
  }
};

export default function ProductPage({ onAddToCart }) {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedImageIndex, setSelectedImageIndex] = useState(0); // Track which image is currently selected
  const [selectedImageKey, setSelectedImageKey] = useState(''); // Track the unique key of the selected image (for mobile)
  const [isMobile, setIsMobile] = useState(false); // Track if we're on mobile
  const [screenWidth, setScreenWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1920); // Track screen width for responsive scaling
  const [fullscreenImage, setFullscreenImage] = useState(null); // Track which image is shown in fullscreen

  // Define image arrays and sizes safely after product is loaded, BEFORE any useEffect
  const smallImages = product ? (product.images.length >= 3 ? [product.images[2], product.images[0]] : product.images) : [];
  const largeLeftImage = product ? (product.images[1] || product.images[0]) : '';
  const mainImage = product ? product.images[0] : '';
  // For 4 images: index 3 should be included. For 5+ images: indices 4+ are extra
  const extraImages = product ? (product.images.length === 4 ? product.images.slice(3) : product.images.slice(4)) : [];
  
  // Market products: no size option, single "One Size" with stock 0 or 1
  const isMarketProduct = product && (product.show_on_market_page === true || product.show_on_market_page === 'true');
  const oneSizeQty = product?.sizes?.find(s => s.size === 'One Size')?.quantity ?? 0;
  // Show all sizes, sorted numerically (we'll grey out out-of-stock ones)
  const allSizes = product ?
    (isMarketProduct ? ['One Size'] :
    (product.sizes && product.sizes.length > 0 ?
      product.sizes.map(s => s.size).sort((a, b) => {
        const numA = parseFloat(a);
        const numB = parseFloat(b);
        if (!isNaN(numA) && !isNaN(numB)) return numA - numB;
        return a.localeCompare(b);
      }) :
      ['7', '8', '9', '10'])
    ) : ['7', '8', '9', '10'];
  // Only sizes that have inventory > 0 (for auto-selection)
  const availableSizes = product ?
    (isMarketProduct ? (oneSizeQty > 0 ? ['One Size'] : []) :
    (product.sizes && product.sizes.length > 0 ?
      product.sizes.filter(s => s.quantity > 0).map(s => s.size).sort((a, b) => {
        const numA = parseFloat(a);
        const numB = parseFloat(b);
        if (!isNaN(numA) && !isNaN(numB)) return numA - numB;
        return a.localeCompare(b);
      }) :
      ['7', '8', '9', '10'])
    ) : ['7', '8', '9', '10'];

  // Get available quantity for selected size
  const getAvailableQuantity = (size) => {
    if (!product || !product.sizes) return 0;
    const sizeData = product.sizes.find(s => s.size === size);
    return sizeData ? sizeData.quantity : 0;
  };

  // Check if selected size is a preorder
  const isPreorderSize = (size) => {
    if (!product || !product.sizes) return false;
    const sizeData = product.sizes.find(s => s.size === size);
    return sizeData ? (sizeData.preorder === true) : false;
  };

  // Check if selected size is made-to-order
  const isMadeToOrderSize = (size) => {
    if (!product || !product.sizes) return false;
    const sizeData = product.sizes.find(s => s.size === size);
    return sizeData ? (sizeData.made_to_order === true) : false;
  };

  // Check if selected size has sufficient inventory
  const hasSufficientInventory = () => {
    const available = getAvailableQuantity(selectedSize);
    return available >= quantity;
  };

  // All hooks at the top!
  const [visibleBoxes, setVisibleBoxes] = useState([]);
  const boxRefs = useRef([]);
  const [hoveredSmall, setHoveredSmall] = useState(null);
  const [selectedSize, setSelectedSize] = useState('');
  const [hoveredSize, setHoveredSize] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const extraBoxRefs = useRef([]);
  const addToCartRef = useRef(null);
  const rightPanelScrollRef = useRef(null);
  /** Inner column of the right panel — ResizeObserver target (scroll container size is fixed, so we must observe content). */
  const rightPanelContentRef = useRef(null);
  const productPageContainerRef = useRef(null);
  /** Synced with window scroll + wheel on description panel (two-phase scroll). */
  const productPhaseProgressRef = useRef(0);
  /** Which product id the scroll phase was last aligned to (avoid inheriting scroll from previous product). */
  const productScrollSyncedIdRef = useRef(null);
  const leftColumnRef = useRef(null);
  const [fixedTopOffset, setFixedTopOffset] = useState(120);
  const [leftColumnTopOffset, setLeftColumnTopOffset] = useState(120);
  const [leftColumnMaxHeight, setLeftColumnMaxHeight] = useState(() => {
    return typeof window !== 'undefined' && window.innerWidth > 768 ? 600 : 600;
  });
  /** Max scroll inside right info panel (description) — drives phase-2 page scroll length */
  const [rightPanelMaxScroll, setRightPanelMaxScroll] = useState(0);
  /** Measured left-column scroll extent (DOM), so spacer matches real image stack height */
  const [measuredLeftMaxScroll, setMeasuredLeftMaxScroll] = useState(0);
  const mobileScrollRef = useRef(null);

  // Create combined array of all images for infinite scroll on mobile
  const allMobileImages = product ? [
    ...smallImages,
    largeLeftImage,
    ...extraImages
  ] : [];

  // Calculate responsive sizes to prevent overlap
  const calculateResponsiveLayout = () => {
    if (isMobile) {
      return {
        leftColumnWidth: 0,
        mainImageWidth: '100%',
        productInfoWidth: '100%',
        leftColumnLeft: 'auto',
        mainImageLeft: 'auto',
        productInfoLeft: 'auto',
        gap: 0
      };
    }

    // Base sizes at 1920px
    const baseLeftWidth = 400;
    const baseMainWidth = 600;
    const baseProductWidth = 400;
    const gapBetweenColumns = 60; // Equal gap between all columns
    
    // Container constraints
    const containerMaxWidth = 1600;
    const containerPadding = 40;
    const containerMargin = 40;
    
    // Calculate container's actual position and width
    // Container is centered with maxWidth 1600, so on wide screens it's centered
    const containerActualWidth = Math.min(containerMaxWidth, screenWidth - (containerMargin * 2));
    const containerLeftEdge = (screenWidth - containerActualWidth) / 2;
    const containerContentLeft = containerLeftEdge + containerPadding;
    const containerContentRight = containerLeftEdge + containerActualWidth - containerPadding;
    const containerContentWidth = containerContentRight - containerContentLeft;
    
    // Calculate total width needed for comparison
    const totalContentWidth = baseLeftWidth + baseMainWidth + baseProductWidth + (gapBetweenColumns * 2);
    
    // If screen is wide enough, use base sizes
    if (screenWidth >= totalContentWidth && containerContentWidth >= totalContentWidth) {
      // Position main image first (centered within container content area)
      // Main image uses translateX(-50%), so left value is the center
      const mainImageLeft = containerContentLeft + (containerContentWidth / 2); // Center of container content
      
      // Calculate main image edges
      const mainImageLeftEdge = mainImageLeft - (baseMainWidth / 2);
      const mainImageRightEdge = mainImageLeft + (baseMainWidth / 2);
      
      // Position left column relative to main image's LEFT EDGE
      // Left column should be gapBetweenColumns to the left of main image's left edge
      const leftColumnRightEdge = mainImageLeftEdge - gapBetweenColumns;
      const leftColumnLeft = leftColumnRightEdge - baseLeftWidth;
      
      // Position product info relative to main image's RIGHT EDGE
      // Product info should be gapBetweenColumns to the right of main image's right edge
      const productInfoLeft = mainImageRightEdge + gapBetweenColumns;
      // Debug logging
      console.log('Layout calculation (base sizes):', {
        screenWidth,
        containerContentLeft,
        containerContentRight,
        containerContentWidth,
        leftColumnLeft,
        leftColumnRightEdge,
        mainImageLeftEdge,
        mainImageLeft,
        mainImageRightEdge,
        productInfoLeft,
        productInfoRight: productInfoLeft + baseProductWidth,
        gapBetweenColumns,
        actualMainImageLeftEdge: mainImageLeft - (baseMainWidth / 2),
        actualMainImageRightEdge: mainImageLeft + (baseMainWidth / 2),
        leftColumnWidth: baseLeftWidth,
        mainImageWidth: baseMainWidth,
        productInfoWidth: baseProductWidth
      });
      
      return {
        leftColumnWidth: baseLeftWidth,
        mainImageWidth: baseMainWidth,
        productInfoWidth: baseProductWidth,
        leftColumnLeft,
        mainImageLeft,
        productInfoLeft: productInfoLeft,
        gap: gapBetweenColumns
      };
    } else {
      // Need to scale down proportionally while maintaining equal gaps
      // Use container content width for available space
      const availableWidthForColumns = containerContentWidth - (gapBetweenColumns * 2);
      
      // Calculate scale factor to fit all columns
      const totalBaseColumnWidth = baseLeftWidth + baseMainWidth + baseProductWidth;
      const scale = availableWidthForColumns / totalBaseColumnWidth;
      
      // Scale all columns proportionally
      const scaledLeftWidth = Math.floor(baseLeftWidth * scale);
      const scaledMainWidth = Math.floor(baseMainWidth * scale);
      const scaledProductWidth = Math.floor(baseProductWidth * scale);
      
      // Position main image first (centered within container content area)
      // Main image uses translateX(-50%), so left value is the center
      const scaledMainImageLeft = containerContentLeft + (containerContentWidth / 2); // Center of container content
      
      // Calculate main image edges
      const scaledMainImageLeftEdge = scaledMainImageLeft - (scaledMainWidth / 2);
      const scaledMainImageRightEdge = scaledMainImageLeft + (scaledMainWidth / 2);
      
      // Position left column relative to main image's LEFT EDGE
      // Left column should be gapBetweenColumns to the left of main image's left edge
      const scaledLeftColumnRightEdge = scaledMainImageLeftEdge - gapBetweenColumns;
      const scaledLeftColumnLeft = scaledLeftColumnRightEdge - scaledLeftWidth;
      
      // Position product info relative to main image's RIGHT EDGE
      // Product info should be gapBetweenColumns to the right of main image's right edge
      const scaledProductInfoLeft = scaledMainImageRightEdge + gapBetweenColumns;
      
      // Debug logging for scaled version
      console.log('Layout calculation (scaled):', {
        screenWidth,
        containerContentLeft,
        containerContentRight,
        containerContentWidth,
        scale,
        scaledLeftColumnLeft,
        scaledLeftColumnRightEdge: scaledLeftColumnRightEdge,
        scaledMainImageLeftEdge,
        scaledMainImageLeft,
        scaledMainImageRightEdge,
        scaledProductInfoLeft,
        scaledProductInfoRight: scaledProductInfoLeft + scaledProductWidth,
        gapBetweenColumns,
        scaledLeftWidth,
        scaledMainWidth,
        scaledProductWidth
      });
      
      return {
        leftColumnWidth: scaledLeftWidth,
        mainImageWidth: scaledMainWidth,
        productInfoWidth: scaledProductWidth,
        leftColumnLeft: scaledLeftColumnLeft,
        mainImageLeft: scaledMainImageLeft,
        productInfoLeft: scaledProductInfoLeft,
        gap: gapBetweenColumns
      };
    }
  };

  // Check if we're on mobile and track screen width
  useEffect(() => {
    const checkScreenSize = () => {
      const width = window.innerWidth;
      setScreenWidth(width);
      setIsMobile(width <= 768);
    };
    
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  const responsiveLayout = calculateResponsiveLayout();

  const mainImageHeightPx = typeof responsiveLayout.mainImageWidth === 'number'
    ? responsiveLayout.mainImageWidth
    : 600;

  useLayoutEffect(() => {
    if (isMobile || !product || !rightPanelScrollRef.current) return;
    const scrollEl = rightPanelScrollRef.current;
    const contentEl = rightPanelContentRef.current;
    const measure = () => {
      const rightExtra = Math.max(0, scrollEl.scrollHeight - scrollEl.clientHeight);
      setRightPanelMaxScroll(rightExtra);
      const left = leftColumnRef.current;
      if (left) {
        setMeasuredLeftMaxScroll(Math.max(0, left.scrollHeight - left.clientHeight));
      }
    };
    measure();
    const ro =
      typeof ResizeObserver !== 'undefined' && contentEl
        ? new ResizeObserver(() => measure())
        : null;
    if (ro && contentEl) ro.observe(contentEl);

    const imgs = contentEl?.querySelectorAll?.('img') ?? [];
    const onImg = () => measure();
    imgs.forEach((img) => {
      if (!img.complete) {
        img.addEventListener('load', onImg);
        img.addEventListener('error', onImg);
      }
    });

    let cancelled = false;
    const fontsReady = typeof document !== 'undefined' && document.fonts?.ready;
    if (fontsReady) {
      fontsReady.then(() => {
        if (!cancelled) measure();
      });
    }

    return () => {
      cancelled = true;
      ro?.disconnect();
      imgs.forEach((img) => {
        img.removeEventListener('load', onImg);
        img.removeEventListener('error', onImg);
      });
    };
  }, [isMobile, product, product?.description, product?.show_on_commission_page, mainImageHeightPx, selectedSize, quantity]);

  // After spacer height updates, re-apply phase mapping so description/images aren’t stuck mid-panel.
  useEffect(() => {
    if (isMobile || !product) return;
    const id = requestAnimationFrame(() => {
      window.dispatchEvent(new Event('scroll'));
    });
    return () => cancelAnimationFrame(id);
  }, [rightPanelMaxScroll, measuredLeftMaxScroll, isMobile, product]);

  // Calculate total height needed for all left column images (shared function)
  const calculateTotalLeftColumnHeight = () => {
    if (!product || !product.images || isMobile) {
      const defaultHeight = responsiveLayout.mainImageWidth || 600;
      console.log('calculateTotalLeftColumnHeight: returning default', defaultHeight);
      return defaultHeight;
    }
    
    const imageCount = product.images.length;
    const leftColWidth = responsiveLayout.leftColumnWidth;
    
    console.log('calculateTotalLeftColumnHeight: imageCount', imageCount, 'leftColWidth', leftColWidth);
    
    // If less than 3 images, all are shown as small images in a row
    if (imageCount < 3) {
      const smallImagesRowHeight = Math.floor(leftColWidth * 0.45) + 12;
      const result = smallImagesRowHeight + 100;
      console.log('calculateTotalLeftColumnHeight: < 3 images, returning', result);
      return result;
    }
    
    // For 3+ images: structure is 2 small images + 1 large + extra images
    const smallImagesRowHeight = Math.floor(leftColWidth * 0.45) + 12;
    const largeImageHeight = leftColWidth;
    // For 4 images: index 3 should be included. For 5+ images: indices 4+ are extra
    const productExtraImages = imageCount === 4 ? product.images.slice(3) : product.images.slice(4);
    const extraImagesHeight = productExtraImages.length > 0 
      ? (productExtraImages.length * leftColWidth) + ((productExtraImages.length - 1) * 24) + 32
      : 0;
    const totalHeight = smallImagesRowHeight + largeImageHeight + extraImagesHeight;
    const result = totalHeight + 100; // Extra 100px buffer
    
    console.log('calculateTotalLeftColumnHeight: breakdown', {
      imageCount,
      smallImagesRowHeight,
      largeImageHeight,
      extraImagesCount: productExtraImages.length,
      extraImagesHeight,
      totalHeight,
      result
    });
    
    return result;
  };

  // Update leftColumnMaxHeight when responsive layout or product changes
  // Always set to full calculated height to allow scrolling through all images
  useEffect(() => {
    if (!isMobile && responsiveLayout.mainImageWidth && product) {
      const totalHeight = calculateTotalLeftColumnHeight();
      console.log('Setting leftColumnMaxHeight to', totalHeight, 'for', product.images?.length, 'images');
      // Always set to the full calculated height, not limited by main image width
      setLeftColumnMaxHeight(totalHeight);
    } else if (!isMobile && !product) {
      // Reset to default when product is not loaded
      setLeftColumnMaxHeight(responsiveLayout.mainImageWidth || 600);
    }
  }, [responsiveLayout.mainImageWidth, responsiveLayout.leftColumnWidth, isMobile, product]);

  // Debug: Log actual scroll height after render
  useEffect(() => {
    if (!isMobile && leftColumnRef.current && product) {
      setTimeout(() => {
        const scrollHeight = leftColumnRef.current?.scrollHeight;
        const clientHeight = leftColumnRef.current?.clientHeight;
        const maxHeight = leftColumnRef.current?.style.maxHeight;
        console.log('Left column debug:', {
          scrollHeight,
          clientHeight,
          maxHeight,
          leftColumnMaxHeight,
          imageCount: product.images?.length,
          canScroll: scrollHeight > clientHeight
        });
      }, 100);
    }
  }, [leftColumnMaxHeight, product, isMobile]);

  // Two-phase desktop scroll: page scroll first drives left (images), then right (title/description)
  useEffect(() => {
    if (isMobile) return;
    if (!product || !leftColumnRef.current) return;

    const left = leftColumnRef.current;
    const right = rightPanelScrollRef.current;
    const container = productPageContainerRef.current;
    if (!left || !container) return;

    const NAV_OFFSET = 120;

    // Convert an element's top position into stable document coordinates.
    const cumulativeOffsetTop = (el) => {
      let top = 0;
      let node = el;
      while (node) {
        top += node.offsetTop;
        node = node.offsetParent;
      }
      return top;
    };

    const scrollBase = Math.max(0, cumulativeOffsetTop(container) - NAV_OFFSET);

    const productScrollKey = product.id != null ? String(product.id) : slug;
    const isNewProduct = productScrollSyncedIdRef.current !== productScrollKey;
    if (isNewProduct) {
      productScrollSyncedIdRef.current = productScrollKey;
      productPhaseProgressRef.current = 0;
      window.scrollTo({ top: scrollBase, left: 0, behavior: 'auto' });
    }

    let lastPageY = window.scrollY || window.pageYOffset;
    if (isNewProduct) {
      lastPageY = scrollBase;
    } else {
      const initialRel = Math.max(0, lastPageY - scrollBase);
      productPhaseProgressRef.current = initialRel;
    }

    const LIFT_MAX = 60; // minimal post-description lift so footer appears soon after description ends

    const getMaxes = () => {
      const leftMax = Math.max(0, left.scrollHeight - left.clientHeight);
      const rightMax = right ? Math.max(0, right.scrollHeight - right.clientHeight) : 0;
      return { leftMax, rightMax, totalMax: leftMax + rightMax + LIFT_MAX };
    };

    const applyPhase = () => {
      let phaseProgress = productPhaseProgressRef.current;
      const { leftMax, rightMax, totalMax } = getMaxes();
      phaseProgress = Math.max(0, Math.min(totalMax, phaseProgress));
      productPhaseProgressRef.current = phaseProgress;

      const contentMax = leftMax + rightMax;
      const contentProgress = Math.min(contentMax, phaseProgress);
      const liftProgress = Math.max(0, phaseProgress - contentMax);

      left.scrollTop = Math.min(leftMax, contentProgress);
      if (right) {
        right.scrollTop = Math.max(0, Math.min(rightMax, contentProgress - leftMax));
      }

      // Phase 3: keep visible movement after right content ends by lifting fixed columns upward.
      const liftOffset = Math.min(LIFT_MAX, liftProgress);
      const newTop = 120 - liftOffset;
      setFixedTopOffset(newTop);
      setLeftColumnTopOffset(newTop);
    };

    applyPhase();

    const syncProductScroll = () => {
      const currentY = window.scrollY || window.pageYOffset;
      const deltaY = currentY - lastPageY;
      lastPageY = currentY;

      // Start phase syncing only once page scroll reaches the product zone.
      if (currentY < scrollBase) {
        productPhaseProgressRef.current = 0;
      } else if (deltaY !== 0) {
        productPhaseProgressRef.current += deltaY;
      }

      applyPhase();
    };

    const handleResize = () => {
      applyPhase();
    };

    // Wheel over description: left column was z-index 100 vs panel 30, so hits went to wrong layer;
    // also page-scroll-only phase never advanced if user only wheels over the panel.
    const onRightPanelWheel = (e) => {
      const rightEl = rightPanelScrollRef.current;
      if (!rightEl || !rightEl.contains(e.target)) return;
      const { leftMax, rightMax, totalMax } = getMaxes();
      if (rightMax <= 0 || left.scrollTop + 1 < leftMax) return;

      const curR = rightEl.scrollTop;
      const nextR = Math.max(0, Math.min(rightMax, curR + e.deltaY));
      if (nextR === curR) return;

      e.preventDefault();
      const contentMax = leftMax + rightMax;
      const liftExtra = Math.max(0, productPhaseProgressRef.current - contentMax);
      productPhaseProgressRef.current = Math.min(totalMax, leftMax + nextR + liftExtra);
      lastPageY = scrollBase + productPhaseProgressRef.current;
      applyPhase();
    };

    window.addEventListener('scroll', syncProductScroll, { passive: true });
    window.addEventListener('resize', handleResize);
    window.addEventListener('wheel', onRightPanelWheel, { passive: false, capture: true });

    return () => {
      window.removeEventListener('scroll', syncProductScroll);
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('wheel', onRightPanelWheel, { capture: true });
    };
  }, [isMobile, product, slug, leftColumnMaxHeight, responsiveLayout.mainImageWidth]);


  // Footer handling is now integrated into phase-based scroll syncing above.

  useEffect(() => {
    setLoading(true);
    fetch(`${config.API_BASE}/api/products/slug/${slug}`)
      .then(res => res.json())
      .then(data => {
        if (data.success) {
          let images = data.product.images;
          if (typeof images === 'string') {
            try { images = JSON.parse(images); } catch { images = images ? [images] : []; }
          }
          if (!Array.isArray(images)) images = images ? [images] : [];
          if (images.length === 0 && data.product.image) images = [data.product.image];
          const productData = { ...data.product, images };
          setProduct(productData);
          
          // Auto-select the first available size (market products use 'One Size' only)
          const isMarket = productData.show_on_market_page === true || productData.show_on_market_page === 'true';
          const availableSizesForSelection = isMarket
            ? (productData.sizes?.find(s => s.size === 'One Size')?.quantity > 0 ? ['One Size'] : [])
            : (productData.sizes && productData.sizes.length > 0 ?
              productData.sizes.filter(s => s.quantity > 0).map(s => s.size).sort((a, b) => {
                const numA = parseFloat(a);
                const numB = parseFloat(b);
                if (!isNaN(numA) && !isNaN(numB)) return numA - numB;
                return a.localeCompare(b);
              }) :
              ['7', '8', '9', '10']);
          setSelectedSize(availableSizesForSelection[0] || (isMarket ? 'One Size' : '7'));
          setError('');
          setSelectedImageIndex(0);
          setSelectedImageKey(''); // Reset selected image key when product changes
          
          // Update SEO meta tags for this product
          updateMetaTags(productData);
          
          // Track product view with Google Analytics
          if (typeof window.gtag === 'function') {
            window.gtag('event', 'view_item', {
              currency: 'USD',
              value: parseFloat(productData.price) || 0,
              items: [{
                item_id: productData.id,
                item_name: productData.name,
                category: productData.categories && productData.categories[0] ? productData.categories[0].name : 'Uncategorized',
                price: parseFloat(productData.price) || 0,
                quantity: 1
              }]
            });
          }
        } else {
          setError('Product not found');
        }
        setLoading(false);
      })
      .catch(() => {
        setError('Failed to fetch product');
        setLoading(false);
      });
  }, [slug]);

  useEffect(() => {
    if (!product) return;
    const observer = new window.IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const idx = Number(entry.target.getAttribute('data-box-idx'));
          if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
            setVisibleBoxes((prev) => (prev.includes(idx) ? prev : [...prev, idx]));
          } else {
            setVisibleBoxes((prev) => prev.filter(i => i !== idx));
          }
        });
      },
      { threshold: 0.5 }
    );
    boxRefs.current.forEach((ref) => {
      if (ref) observer.observe(ref);
    });
    return () => observer.disconnect();
  }, [product]);

  useEffect(() => {
    if (!product) return;
    const observer = new window.IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const idx = Number(entry.target.getAttribute('data-box-idx'));
          if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
            setVisibleBoxes((prev) => (prev.includes(idx) ? prev : [...prev, idx]));
          } else {
            setVisibleBoxes((prev) => prev.filter(i => i !== idx));
          }
        });
      },
      { threshold: 0.5 }
    );
    extraBoxRefs.current.forEach((ref) => {
      if (ref) observer.observe(ref);
    });
    return () => observer.disconnect();
  }, [product, extraImages.length]);

  // Handle image selection for mobile
  const handleImageSelect = (imageIndex, imageKey) => {
    if (isMobile) {
      // For mobile, use the index directly in allMobileImages array
      setSelectedImageIndex(imageIndex);
      setSelectedImageKey(imageKey || '');
    }
  };

  // Handle image click to open fullscreen
  const handleImageClick = (imageUrl) => {
    // Only enable fullscreen on desktop, not mobile
    if (!isMobile) {
      setFullscreenImage(imageUrl);
    }
  };

  // Close fullscreen
  const closeFullscreen = () => {
    setFullscreenImage(null);
  };

  // Hide navbar when fullscreen is open
  useEffect(() => {
    const nav = document.querySelector('nav');
    if (fullscreenImage && nav) {
      nav.style.display = 'none';
    } else if (nav) {
      nav.style.display = '';
    }
    return () => {
      // Cleanup: restore navbar when component unmounts
      const nav = document.querySelector('nav');
      if (nav) {
        nav.style.display = '';
      }
    };
  }, [fullscreenImage]);

  // Get the currently selected image
  const getSelectedImage = () => {
    if (!product) return '';
    
    if (isMobile) {
      // On mobile, use the image directly from allMobileImages array
      // allMobileImages = [smallImages..., largeLeftImage, extraImages...]
      // So we can use selectedImageIndex directly
      if (selectedImageIndex >= 0 && selectedImageIndex < allMobileImages.length) {
        return allMobileImages[selectedImageIndex];
      }
      return allMobileImages[0] || product.images[0];
    }
    
    // Desktop: use the original logic
    const allImages = [
      product.images[0], // Main image
      product.images[1] || product.images[0], // Large left image
      product.images[2] || product.images[0], // First small image
      ...extraImages // Extra images
    ];
    
    return allImages[selectedImageIndex] || product.images[0];
  };

  if (loading) return <div>Loading...</div>;
  if (error || !product) return <div style={{ color: 'red' }}>{error || 'Product not found'}</div>;

  const UPLOADS_BASE = config.UPLOADS_BASE || config.API_BASE;
  const getUrl = (img, addCacheBust = true) => {
    if (!img) return '';
    let url = '';
    if (img.startsWith('/uploads/')) {
      url = `${UPLOADS_BASE}${img}`;
    } else if (!img.startsWith('http')) {
      url = `${UPLOADS_BASE}/uploads/${img.replace(/^\/+/, '')}`;
    } else {
      url = img;
    }
    // Add cache-busting parameter to ensure images refresh on mobile and desktop
    if (addCacheBust && product) {
      let cacheBust;
      if (product.updated_at) {
        cacheBust = new Date(product.updated_at).getTime();
      } else if (product.id) {
        // Fallback to using product ID and current hour for cache-busting
        const hour = Math.floor(Date.now() / (1000 * 60 * 60)); // Changes every hour
        cacheBust = `${product.id}-${hour}`;
      } else {
        // Last resort: use current timestamp
        cacheBust = Date.now();
      }
      return `${url}${url.includes('?') ? '&' : '?'}v=${cacheBust}`;
    }
    return url;
  };

  const handleAddToCart = () => {
    const sizeForCart = isMarketProduct ? 'One Size' : selectedSize;
    const qtyForCart = isMarketProduct ? 1 : quantity;
    const cartItem = {
      ...product,
      selectedSize: sizeForCart,
      quantity: qtyForCart
    };
    
    // Track add to cart with Google Analytics
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'add_to_cart', {
        currency: 'USD',
        value: parseFloat(product.price) * qtyForCart,
        items: [{
          item_id: product.id,
          item_name: product.name,
          category: product.categories && product.categories[0] ? product.categories[0].name : 'Uncategorized',
          quantity: qtyForCart,
          price: parseFloat(product.price) || 0
        }]
      });
    }
    
    onAddToCart(cartItem);
  };

  const handleQuantityChange = (newQuantity) => {
    const available = getAvailableQuantity(selectedSize);
    const maxQuantity = Math.min(99, available);
    setQuantity(Math.max(1, Math.min(maxQuantity, newQuantity)));
  };

  // Calculate total scrollable height needed for all images in left column
  // Works for any number of images using responsive sizes
  const calculateLeftColumnScrollHeight = () => {
    if (!product || !product.images) return 0;
    if (isMobile) return 0;
    
    const imageCount = product.images.length;
    const leftColWidth = responsiveLayout.leftColumnWidth;
    const mainImgWidth = responsiveLayout.mainImageWidth;
    
    // If less than 3 images, all are shown as small images in a row
    if (imageCount < 3) {
      const smallImagesRowHeight = Math.floor(leftColWidth * 0.45) + 12; // Small images + margin
      const totalContentHeight = smallImagesRowHeight;
      // Return the amount that exceeds the container (scrollable portion)
      return Math.max(0, totalContentHeight - mainImgWidth) + 150; // Extra 150px buffer for footer clearance
    }
    
    // For 3+ images: structure is 2 small images + 1 large + extra images
    const smallImagesRowHeight = Math.floor(leftColWidth * 0.45) + 12; // Small images + margin
    const largeImageHeight = leftColWidth;
    // Use the same logic as extraImages: for 4 images include index 3, for 5+ include indices 4+
    const calculatedExtraImages = imageCount === 4 ? product.images.slice(3) : product.images.slice(4);
    const extraImagesHeight = calculatedExtraImages.length > 0 
      ? (calculatedExtraImages.length * leftColWidth) + ((calculatedExtraImages.length - 1) * 24) + 32 // images + gaps + top margin
      : 0;
    const totalContentHeight = smallImagesRowHeight + largeImageHeight + extraImagesHeight;
    // Return the amount that exceeds the container (scrollable portion)
    return Math.max(0, totalContentHeight - mainImgWidth) + 150; // Extra 150px buffer for footer clearance
  };

  const scrollableContentHeight = calculateLeftColumnScrollHeight();
  const leftScrollBudget = Math.max(scrollableContentHeight, measuredLeftMaxScroll);
  // Extra tail so document scroll always exceeds two-phase range (long HTML descriptions + images in copy).
  const pageSpacerHeight = 320 + leftScrollBudget + rightPanelMaxScroll + Math.max(320, Math.floor(rightPanelMaxScroll * 0.08));

  return (
    <div ref={productPageContainerRef} className="product-page-container" style={{ width: '100%', maxWidth: 1600, margin: '40px auto', padding: isMobile ? '8px 16px' : '8px 40px', borderRadius: 12, background: '#fff', minHeight: 700, position: isMobile ? 'relative' : 'sticky', top: isMobile ? 'auto' : 0, zIndex: isMobile ? 1 : 10, overflowX: 'hidden', boxSizing: 'border-box' }}>
      <div className="product-page-layout" style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: 'flex-start', minHeight: isMobile ? 'auto' : responsiveLayout.mainImageWidth, height: 'auto', gap: 32, justifyContent: 'space-between', position: 'relative' }}>
        {/* Left column: all other images, scrollable if needed */}
        {/* Main image is fixed at top: 120px with height: 600px, so bottom is at 720px */}
        {/* Left column should never scroll past this point, so max-height is 600px */}
        {/* Use height instead of max-height and ensure it scrolls internally */}
        <div 
          ref={leftColumnRef}
          style={{ 
            display: isMobile ? 'none' : 'flex', 
            flexDirection: 'column', 
            alignItems: 'flex-end', 
            margin: 0, 
            padding: 0, 
            width: responsiveLayout.leftColumnWidth, 
            boxSizing: 'border-box', 
            background: 'transparent', 
            scrollbarWidth: 'none', 
            msOverflowStyle: 'none', 
            zIndex: 100, 
            position: 'fixed', 
            left: responsiveLayout.leftColumnLeft, 
            top: leftColumnTopOffset, 
            alignSelf: 'flex-start', 
            minHeight: responsiveLayout.mainImageWidth, 
            maxHeight: `${leftColumnMaxHeight}px`, 
            height: `${responsiveLayout.mainImageWidth}px`, // Fixed height = visible area, content can scroll
            overflowY: 'auto', 
            overflowX: 'visible'
          }}
          className="hide-scrollbar">
          {!isMobile && (
            // Desktop: Original layout
            <>
              <div style={{ display: 'flex', flexDirection: 'row', gap: 12, marginBottom: 12, width: responsiveLayout.leftColumnWidth, justifyContent: 'space-between', flexWrap: 'nowrap' }}>
                {smallImages.map((img, idx) => {
                  const imageUrl = getUrl(img);
                  const imageKey = `small-${idx}`;
                  const isSelected = isMobile && selectedImageKey === imageKey;
                  const uniqueKey = `${product.id}-small-${idx}-${img}`;
                  return (
                    <img
                      key={uniqueKey}
                      src={imageUrl}
                      alt={product.name + ' small ' + (idx + 1)}
                      onMouseEnter={() => setHoveredSmall(idx)}
                      onMouseLeave={() => setHoveredSmall(null)}
                      onClick={() => {
                        handleImageSelect(idx + 2, imageKey);
                        handleImageClick(imageUrl);
                      }}
                      onError={(e) => {
                        const fallbackUrl = getUrl(img, false);
                        if (e.target.src !== fallbackUrl) {
                          e.target.src = fallbackUrl;
                        }
                      }}
                      style={{
                        width: hoveredSmall === idx ? responsiveLayout.leftColumnWidth : hoveredSmall === null ? Math.floor(responsiveLayout.leftColumnWidth * 0.45) : 0,
                        height: hoveredSmall === idx ? responsiveLayout.leftColumnWidth : hoveredSmall === null ? Math.floor(responsiveLayout.leftColumnWidth * 0.45) : 0,
                        objectFit: 'cover',
                        borderRadius: 4,
                        background: '#fff',
                        display: 'block',
                        transition: 'width 0.2s, height 0.2s',
                        zIndex: hoveredSmall === idx ? 200 : 1,
                        cursor: 'pointer',
                        border: isSelected ? '1px solid #000' : 'none',
                        boxSizing: 'border-box',
                        position: 'relative',
                        flexShrink: 0,
                      }}
                    />
                  );
                })}
              </div>
              <div style={{ width: responsiveLayout.leftColumnWidth, height: responsiveLayout.leftColumnWidth, display: 'flex', alignItems: 'flex-end', justifyContent: 'center', margin: 0, padding: 0, position: 'relative', zIndex: 150 }}>
                {(() => {
                  const imageUrl = getUrl(largeLeftImage);
                  const imageKey = 'large-left';
                  const isSelected = isMobile && selectedImageKey === imageKey;
                  const uniqueKey = `${product.id}-large-left-${largeLeftImage}`;
                  return (
                    <img
                      key={uniqueKey}
                      src={imageUrl}
                      alt={product.name + ' 2'}
                      onClick={() => {
                        handleImageSelect(1, imageKey);
                        handleImageClick(imageUrl);
                      }}
                      onError={(e) => {
                        const fallbackUrl = getUrl(largeLeftImage, false);
                        if (e.target.src !== fallbackUrl) {
                          e.target.src = fallbackUrl;
                        }
                      }}
                      style={{ 
                        width: responsiveLayout.leftColumnWidth, 
                        height: responsiveLayout.leftColumnWidth, 
                        objectFit: 'cover', 
                        borderRadius: 4, 
                        background: '#fff', 
                        display: 'block',
                        cursor: 'pointer',
                        border: isSelected ? '1px solid #000' : 'none',
                        boxSizing: 'border-box',
                        position: 'relative',
                        zIndex: 150,
                        flexShrink: 0,
                      }}
                    />
                  );
                })()}
              </div>
              {extraImages.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 24, marginTop: 8, alignItems: 'flex-end', width: responsiveLayout.leftColumnWidth, flexWrap: 'nowrap' }}>
                  {extraImages.map((img, idx) => {
                    const imageUrl = getUrl(img);
                    const imageKey = `extra-${idx}`;
                    const isSelected = isMobile && selectedImageKey === imageKey;
                    const uniqueKey = `${product.id}-extra-${idx}-${img}`;
                    return (
                      <img
                        key={uniqueKey}
                        ref={el => extraBoxRefs.current[idx] = el}
                        data-box-idx={idx}
                        src={imageUrl}
                        alt={product.name + ' extra ' + (idx + 5)}
                        onClick={() => {
                          handleImageSelect(idx + 3, imageKey);
                          handleImageClick(imageUrl);
                        }}
                        onError={(e) => {
                          const fallbackUrl = getUrl(img, false);
                          if (e.target.src !== fallbackUrl) {
                            e.target.src = fallbackUrl;
                          }
                        }}
                        style={{
                          width: responsiveLayout.leftColumnWidth,
                          height: responsiveLayout.leftColumnWidth,
                          objectFit: 'cover',
                          borderRadius: 4,
                          background: '#fff',
                          display: 'block',
                          opacity: visibleBoxes.includes(idx) ? 1 : 0,
                          transition: 'opacity 0.2s ease-in',
                          cursor: 'pointer',
                          border: isSelected ? '1px solid #000' : 'none',
                          boxSizing: 'border-box',
                          flexShrink: 0,
                        }}
                      />
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
        {/* Main image - fixed, adjusts position when footer approaches */}
        <div style={{ width: isMobile ? '100%' : responsiveLayout.mainImageWidth, height: isMobile ? 'auto' : responsiveLayout.mainImageWidth, display: 'flex', alignItems: 'center', justifyContent: 'center', position: isMobile ? 'relative' : 'fixed', top: isMobile ? 'auto' : fixedTopOffset, left: isMobile ? 'auto' : responsiveLayout.mainImageLeft, transform: isMobile ? 'none' : 'translateX(-50%)', background: '#fff', zIndex: isMobile ? 1 : 20, borderRadius: 4, margin: isMobile ? '20px 0' : 0 }}>
          <img
            key={`main-${product.id}-${product.images[0]}-${isMobile ? selectedImageIndex : 0}`}
            src={getUrl(isMobile ? getSelectedImage() : product.images[0])}
            alt={product.name + ' main'}
            onClick={() => {
              if (!isMobile) {
                handleImageClick(getUrl(product.images[0]));
              }
            }}
            onError={(e) => {
              // If image fails, try without cache-busting
              const fallbackUrl = getUrl(isMobile ? getSelectedImage() : product.images[0], false);
              if (e.target.src !== fallbackUrl) {
                e.target.src = fallbackUrl;
              }
            }}
            style={{ 
              width: isMobile ? '100%' : responsiveLayout.mainImageWidth, 
              height: isMobile ? 'auto' : responsiveLayout.mainImageWidth, 
              maxHeight: isMobile ? '70vh' : responsiveLayout.mainImageWidth,
              objectFit: isMobile ? 'contain' : 'cover', 
              borderRadius: 4, 
              background: '#fff', 
              display: 'block', 
              margin: 0, 
              padding: 0,
              cursor: 'pointer'
            }}
          />
        </div>
        {/* Mobile: Image carousel below main image */}
        {isMobile && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              gap: 12,
              overflowX: 'auto',
              overflowY: 'hidden',
              width: '100vw',
              padding: '0 16px',
              marginLeft: 'calc(-50vw + 50%)',
              marginTop: '20px',
              WebkitOverflowScrolling: 'touch',
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
            }}
            className="hide-scrollbar"
          >
            {allMobileImages.map((img, idx) => {
              const imageUrl = getUrl(img);
              const imageKey = idx < smallImages.length 
                ? `small-${idx}` 
                : idx === smallImages.length 
                  ? 'large-left' 
                  : `extra-${idx - smallImages.length - 1}`;
              const isSelected = selectedImageKey === imageKey;
              // Use the index directly in allMobileImages array for mobile
              const selectIndex = idx;
              
              // Use image URL as part of key to force re-render when images change
              const uniqueKey = `${product.id}-${imageKey}-${img}-${idx}`;
              
              return (
                <img
                  key={uniqueKey}
                  src={imageUrl}
                  alt={product.name + ' ' + (idx + 1)}
                  onClick={() => {
                    handleImageSelect(selectIndex, imageKey);
                  }}
                  onLoad={() => {
                    // Force image refresh on mobile by removing and re-adding to cache
                    if (isMobile) {
                      // Image loaded successfully
                    }
                  }}
                  onError={(e) => {
                    // If image fails, try without cache-busting
                    const fallbackUrl = getUrl(img, false);
                    if (e.target.src !== fallbackUrl) {
                      e.target.src = fallbackUrl;
                    }
                  }}
                  style={{
                    width: 150,
                    height: 150,
                    minWidth: 150,
                    flexShrink: 0,
                    objectFit: 'contain',
                    borderRadius: 12,
                    background: '#fff',
                    display: 'block',
                    cursor: 'pointer',
                    border: isSelected ? '1px solid #000' : 'none',
                    boxSizing: 'border-box',
                  }}
                />
              );
            })}
          </div>
        )}
        {/* Product description - fixed outer; inner scrolls after images (desktop two-phase scroll) */}
        <div
          ref={addToCartRef}
          data-panel="product-info"
          style={{
            maxWidth: isMobile ? '100%' : responsiveLayout.productInfoWidth,
            width: isMobile ? '100%' : responsiveLayout.productInfoWidth,
            minWidth: isMobile ? 'auto' : responsiveLayout.productInfoWidth,
            minHeight: isMobile ? 'auto' : responsiveLayout.mainImageWidth,
            height: isMobile ? 'auto' : responsiveLayout.mainImageWidth,
            maxHeight: isMobile ? 'none' : responsiveLayout.mainImageWidth,
            padding: isMobile ? '0' : '0 24px',
            background: '#fff',
            borderRadius: 16,
            /* Above left thumbnails column (z-index 100) so picker / wheel hit the description, not a full-width ancestor */
            zIndex: isMobile ? 1 : 160,
            display: 'flex',
            flexDirection: 'column',
            alignItems: isMobile ? 'flex-start' : 'stretch',
            justifyContent: 'flex-start',
            position: isMobile ? 'relative' : 'fixed',
            top: isMobile ? 'auto' : fixedTopOffset,
            left: isMobile ? 'auto' : responsiveLayout.productInfoLeft,
            boxSizing: 'border-box',
            overflow: isMobile ? 'visible' : 'hidden',
          }}
        >
          <div
            ref={rightPanelScrollRef}
            className={isMobile ? undefined : 'hide-scrollbar product-page-right-panel-scroll'}
            style={{
              width: '100%',
              /* flex-basis 0 + minHeight 0: bounded height so overflow-y auto can scroll full description */
              flex: isMobile ? 'none' : '1 1 0%',
              minHeight: isMobile ? 'auto' : 0,
              maxHeight: isMobile ? 'none' : '100%',
              overflowY: isMobile ? 'visible' : 'auto',
              overflowX: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              gap: 0,
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              boxSizing: 'border-box',
              paddingBottom: isMobile ? 0 : 48,
            }}
          >
          <div
            ref={rightPanelContentRef}
            style={{
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              gap: 20,
              minHeight: 'min-content',
            }}
          >
          <div style={{ width: '100%' }}>
            <h2 style={{ margin: 0, marginBottom: 16, fontWeight: 'normal' }}>{product.name}</h2>
            {product.show_on_commission_page && (
              <div style={{ margin: 0, fontSize: 18, marginTop: 0 }} dangerouslySetInnerHTML={{ __html: sanitizeProductDescription(product.description || '') }} />
            )}
          </div>
          {!product.show_on_commission_page && (
            <div style={{ width: '100%' }}>
              {(() => {
                // Calculate scale factor for buttons to maintain aspect ratio
                const baseProductWidth = 400;
                const buttonScale = isMobile ? 1 : Math.max(0.8, Math.min(1, responsiveLayout.productInfoWidth / baseProductWidth));
                const sizeButtonSize = 48 * buttonScale;
                const sizeButtonFontSize = 18 * buttonScale;
                const sizeButtonBorderWidth = 2 * buttonScale;
                const sizeButtonGap = 16 * buttonScale;
                
                return (
                  <>
                    <div style={{ fontWeight: 'bold', fontSize: 22, marginBottom: 16 }}>${product.price.toFixed(2)}</div>
                    {!isMarketProduct && (
                      <>
                        <div style={{ fontWeight: 'bold', marginBottom: 8, fontSize: 16 }}>Size:</div>
                        <div style={{ display: 'flex', flexDirection: 'row', gap: sizeButtonGap, marginBottom: 16, justifyContent: 'center', width: '100%' }}>
                          {allSizes.map(size => {
                            const isSelected = selectedSize === size;
                            const isAvailable = getAvailableQuantity(size) > 0;
                            return (
                              <div
                                key={size}
                                onClick={() => { if (isAvailable) setSelectedSize(size); }}
                                onMouseEnter={() => { if (isAvailable) setHoveredSize(size); }}
                                onMouseLeave={() => setHoveredSize(null)}
                                style={{
                                  width: sizeButtonSize,
                                  height: sizeButtonSize,
                                  borderRadius: '50%',
                                  background: isSelected ? '#000' : (isAvailable ? 'transparent' : '#f5f5f5'),
                                  color: isSelected ? '#fff' : (isAvailable ? '#000' : '#ccc'),
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontWeight: 'bold',
                                  fontSize: sizeButtonFontSize,
                                  cursor: isAvailable ? 'pointer' : 'not-allowed',
                                  border: `${sizeButtonBorderWidth}px solid ${isAvailable ? '#000' : '#ddd'}`,
                                  transition: 'background 0.2s, color 0.2s, border 0.2s, transform 0.15s',
                                  transform: (hoveredSize === size && isAvailable) ? 'scale(1.02)' : 'scale(1)',
                                  position: 'relative',
                                  opacity: isAvailable ? 1 : 0.5,
                                  flexShrink: 0,
                                }}
                              >
                                <span style={{ fontSize: `${16 * buttonScale}px`, lineHeight: 1 }}>{size}</span>
                              </div>
                            );
                          })}
                        </div>
                      </>
                    )}
                  </>
                );
              })()}

              {!isMarketProduct && (
                <>
              <div style={{ fontWeight: 'bold', marginBottom: 8, fontSize: 16 }}>Quantity:</div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16, gap: 12 }}>
                <button
                  onClick={() => handleQuantityChange(quantity - 1)}
                  disabled={quantity <= 1}
                  style={{
                    width: 25,
                    height: 25,
                    borderRadius: '50%',
                    background: quantity <= 1 ? '#ccc' : '#000',
                    color: '#fff',
                    border: 'none',
                    fontSize: 15,
                    fontWeight: 'bold',
                    cursor: quantity <= 1 ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    lineHeight: '25px',
                    padding: 0,
                    transition: 'background 0.2s, transform 0.15s',
                  }}
                  onMouseEnter={(e) => {
                    if (quantity > 1) e.target.style.background = '#333';
                  }}
                  onMouseLeave={(e) => {
                    if (quantity > 1) e.target.style.background = '#000';
                  }}
                >
                  -
                </button>
                <div style={{
                  width: 30,
                  height: 40,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 18,
                  fontWeight: 'bold',
                  border: '2px solid #000',
                  borderRadius: 8,
                  background: '#fff',
                  color: '#000'
                }}>
                  {quantity}
                </div>
                <button
                  onClick={() => handleQuantityChange(quantity + 1)}
                  disabled={quantity >= getAvailableQuantity(selectedSize)}
                  style={{
                    width: 25,
                    height: 25,
                    borderRadius: '50%',
                    background: quantity >= getAvailableQuantity(selectedSize) ? '#ccc' : '#000',
                    color: '#fff',
                    border: 'none',
                    fontSize: 15,
                    fontWeight: 'bold',
                    cursor: quantity >= getAvailableQuantity(selectedSize) ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    lineHeight: '25px',
                    padding: 0,
                    transition: 'background 0.2s, transform 0.15s',
                  }}
                  onMouseEnter={(e) => {
                    if (quantity < getAvailableQuantity(selectedSize)) e.target.style.background = '#333';
                  }}
                  onMouseLeave={(e) => {
                    if (quantity < getAvailableQuantity(selectedSize)) e.target.style.background = '#000';
                  }}
                >
                  +
                </button>
              </div>
                </>
              )}

              {getAvailableQuantity(isMarketProduct ? 'One Size' : selectedSize) > 0 ? (
                <button 
                  onClick={handleAddToCart}
                  style={{ 
                    width: '100%', 
                    padding: '14px 0', 
                    fontSize: 18, 
                    fontWeight: 'bold', 
                    borderRadius: 2, 
                    background: '#222', 
                    color: '#fff', 
                    border: 'none', 
                    cursor: 'pointer',
                    transition: 'background 0.2s'
                  }}
                  onMouseEnter={(e) => e.target.style.background = '#000'}
                  onMouseLeave={(e) => e.target.style.background = '#222'}
                >
                  Add to Cart
                </button>
              ) : (
                <div style={{
                  width: '100%',
                  padding: '14px 0',
                  fontSize: 18,
                  fontWeight: 'bold',
                  borderRadius: 2,
                  background: '#f5f5f5',
                  color: '#999',
                  border: '2px solid #ddd',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'not-allowed'
                }}>
                  Out of Stock
                </div>
              )}
            </div>
          )}
          {!product.show_on_commission_page && (
            <div style={{ width: '100%', marginTop: 20 }}>
              <div style={{ margin: 0, fontSize: 18 }} dangerouslySetInnerHTML={{ __html: sanitizeProductDescription(product.description || '') }} />
              {isPreorderSize(isMarketProduct ? 'One Size' : selectedSize) && getAvailableQuantity(isMarketProduct ? 'One Size' : selectedSize) > 0 && (
                <div style={{ margin: 0, fontSize: 18, marginTop: 20 }}>
                  <strong>Preorder Item:</strong> This item is available for preorder and will be manufactured before shipping. Please allow additional time for delivery.
                </div>
              )}
              {isMadeToOrderSize(isMarketProduct ? 'One Size' : selectedSize) && getAvailableQuantity(isMarketProduct ? 'One Size' : selectedSize) > 0 && (
                <div style={{ margin: 0, fontSize: 18, marginTop: 20 }}>
                  <strong>Made-to-Order Item:</strong> This product is made-to-order. Please allow a production window of 3–4 weeks before your order is shipped. You will receive a tracking number as soon as your piece is ready to head your way.
                </div>
              )}
            </div>
          )}
          </div>
          </div>
        </div>
      </div>
      {/* Spacer to ensure footer has space and product container can scroll with footer */}
      {/* Calculate spacer height based on scrollable content to ensure footer appears below all images */}
      {/* The spacer needs to account for: base spacing (200px) + any scrollable content beyond the 600px container + extra clearance for last image */}
      <div className="product-page-spacer" style={{ 
        height: `${pageSpacerHeight}px`,
        minHeight: `${pageSpacerHeight}px`,
        width: '100%',
        pointerEvents: 'none',
        position: 'relative',
        /* Below .product-page-layout so we don't paint white over fixed images/description (same viewport band on tall screens). */
        zIndex: isMobile ? 0 : -1
      }} />
      {/* Fullscreen image viewer */}
      {fullscreenImage && (
        <div
          onClick={closeFullscreen}
          style={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100vw',
              height: '100vh',
              background: '#000',
              zIndex: 10000,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              overflow: 'hidden'
            }}
          >
            <button
              onClick={(e) => {
                e.stopPropagation();
                closeFullscreen();
              }}
              style={{
                position: 'fixed',
                top: 20,
                right: 20,
                background: '#fff',
                border: '2px solid #000',
                borderRadius: '50%',
                width: 50,
                height: 50,
                fontSize: 32,
                fontWeight: 'bold',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#000',
                zIndex: 10001,
                transition: 'background 0.2s, transform 0.2s',
                lineHeight: 1,
                padding: 0,
                boxShadow: '0 2px 8px rgba(0,0,0,0.3)'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = '#f0f0f0';
                e.target.style.transform = 'scale(1.1)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = '#fff';
                e.target.style.transform = 'scale(1)';
              }}
              aria-label="Close fullscreen"
            >
              ×
            </button>
            <img
              src={fullscreenImage}
              alt={product ? product.name + ' fullscreen' : 'Product image fullscreen'}
              onClick={(e) => e.stopPropagation()}
              style={{
                width: '100vw',
                height: '100vh',
                objectFit: 'contain',
                objectPosition: 'center'
              }}
            />
          </div>
      )}
      <style>{`
        /* Product page container is sticky, so it scrolls with footer when footer approaches */
        .product-page-container {
          position: sticky !important;
          top: 0 !important;
          align-self: flex-start !important;
        }
        
        /* Ensure left column stays within viewport and never goes past main image bottom */
        /* Main image is 600px tall starting at top: 120px, so left column max-height is 600px */
        /* Use min-height and max-height to allow content to expand but clip at 600px for scrolling */
        /* Note: position and top are set in inline styles, don't override them */
        /* CRITICAL: Left column scrolling - must be at the end to override other rules */
        .product-page-layout > div:first-child.hide-scrollbar,
        .product-page-layout > div:first-child {
          /* position and top are set via inline styles - don't override */
          align-self: flex-start !important;
          min-height: 600px !important;
          /* max-height and height are set dynamically via inline styles - don't override with !important */
          /* DO NOT set max-height or height here - they're set via inline styles to allow all images to scroll */
          /* CRITICAL: Force overflow-y to auto - this enables scrolling - MUST be last to override */
          overflow-y: auto !important;
          overflow-x: visible !important;
          z-index: 100 !important;
          background: transparent !important;
          /* DO NOT set height: auto !important - it prevents scrolling by forcing container to expand */
        }
        
        /* Create an inner scrollable container that allows overflow */
        .product-page-layout > div:first-child::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          pointer-events: none;
          z-index: -1;
        }
        
        /* Ensure expanded images in left column don't clip */
        .product-page-layout > div:first-child > div > img {
          position: relative !important;
        }
        
        .product-page-layout > div:first-child > div > img[style*="width: 400px"] {
          z-index: 200 !important;
        }
        
        /* Ensure large left image doesn't clip when small images expand */
        .product-page-layout > div:first-child > div:nth-child(2) {
          z-index: 150 !important;
          position: relative !important;
        }
        
        .product-page-layout > div:first-child > div:nth-child(2) > img {
          z-index: 150 !important;
          position: relative !important;
        }
        
        /* Ensure expanded small images can overflow the container */
        .product-page-layout > div:first-child > div:first-child {
          position: relative !important;
          z-index: 200 !important;
        }
        
        /* Override overflow clipping - allow content to overflow container bounds */
        /* The container still scrolls but doesn't clip overflowing content */
        /* Note: position is set to fixed in inline styles, don't override it */
        .product-page-layout > div:first-child {
          /* CRITICAL: Force overflow-y to auto for scrolling - this MUST override any other overflow rules */
          overflow-y: auto !important;
          overflow-x: visible !important;
          /* position is fixed via inline styles - don't override */
          clip-path: none !important;
        }
        
        /* Ensure the spacer doesn't create a clipping boundary */
        .product-page-spacer {
          overflow: visible !important;
          clip-path: none !important;
        }

        /* Spacer follows layout in DOM → same stacking level it paints ON TOP of fixed columns in the overlapping viewport band. */
        @media (min-width: 769px) {
          .product-page-layout {
            position: relative !important;
            z-index: 1 !important;
          }
          .product-page-spacer {
            z-index: -1 !important;
          }
        }
        
        /* Ensure product container doesn't clip */
        .product-page-container {
          overflow: visible !important;
          clip-path: none !important;
        }
        
        /* Prevent horizontal scrolling on mobile for the page, but allow it for image gallery */
        @media (max-width: 768px) {
          .product-page-container {
            overflow-x: hidden !important;
            width: 100% !important;
            max-width: 100vw !important;
            box-sizing: border-box !important;
          }
          
          body {
            overflow-x: hidden !important;
          }
          
          .product-page-layout {
            width: 100% !important;
            max-width: 100% !important;
            box-sizing: border-box !important;
          }
          
          /* Allow horizontal scrolling only for the image gallery container */
          .product-page-layout > div:first-child {
            overflow-x: auto !important;
            overflow-y: auto !important; /* Ensure vertical scrolling still works on mobile */
          }
        }
        
        /* Ensure footer stays below all fixed elements and has proper z-index */
        footer {
          position: relative !important;
          z-index: 100 !important;
          background: #fff !important;
          margin-top: 100px !important; /* Extra space to prevent last image from being hidden */
        }
        
        /* On mobile, ensure footer appears after product content */
        @media (max-width: 768px) {
          footer {
            position: relative !important;
            z-index: 1 !important;
            margin-top: 40px !important;
          }
          
          .product-page-container {
            margin-bottom: 40px !important;
          }
        }
        
        /* Main image and add to cart button - position controlled by shouldScrollWithFooter state */
        /* When footer approaches, these become sticky and scroll up with the footer */
        
        /* Responsive Design for Product Page */
        @media (max-width: 1200px) {
          .product-page-container {
            padding: 8px 20px !important;
          }
          
          .product-page-layout {
            gap: 20px !important;
          }
        }
        
        @media (max-width: 768px) {
          .product-page-container {
            margin: 20px auto !important;
            padding: 8px 16px !important;
            min-height: auto !important;
            position: relative !important;
            z-index: 1 !important;
          }
          
          .product-page-layout {
            flex-direction: column !important;
            gap: 20px !important;
            min-height: auto !important;
            height: auto !important;
          }
          
          /* Left column - make it horizontal scrollable bar on top */
          .product-page-layout > div:first-child {
            width: 100vw !important;
            max-width: 100vw !important;
            margin-right: 0 !important;
            margin-left: calc(-50vw + 50%) !important;
            overflow-x: auto !important;
            overflow-y: hidden !important;
            flex-direction: row !important;
            gap: 12px !important;
            padding: 0 0 12px 0 !important;
            margin-bottom: 0 !important;
            justify-content: flex-start !important;
            position: relative !important;
            top: auto !important;
            max-height: none !important;
            min-height: auto !important;
            align-self: flex-start !important;
            flex-wrap: nowrap !important;
            -webkit-overflow-scrolling: touch !important;
            scroll-behavior: smooth !important;
          }
          
          .product-page-layout > div:first-child > div:first-child {
            flex-shrink: 0 !important;
            width: 150px !important;
            margin-bottom: 0 !important;
            display: flex !important;
            flex-direction: row !important;
            gap: 12px !important;
          }
          
          .product-page-layout > div:first-child > div:first-child img {
            width: 150px !important;
            height: 150px !important;
            border-radius: 4px !important;
            flex-shrink: 0 !important;
            object-fit: contain !important;
            box-sizing: border-box !important;
          }
          
          .product-page-layout > div:first-child > div:nth-child(2) {
            width: 150px !important;
            height: 150px !important;
            flex-shrink: 0 !important;
            margin: 0 !important;
            display: flex !important;
            align-items: center !important;
            justify-content: center !important;
          }
          
          .product-page-layout > div:first-child > div:nth-child(2) img {
            width: 150px !important;
            height: 150px !important;
            border-radius: 4px !important;
            display: block !important;
            object-fit: contain !important;
            box-sizing: border-box !important;
          }
          
          .product-page-layout > div:first-child > div:last-child {
            width: auto !important;
            flex-shrink: 0 !important;
            margin-top: 0 !important;
            gap: 12px !important;
            flex-direction: row !important;
            display: flex !important;
          }
          
          .product-page-layout > div:first-child > div:last-child img {
            width: 150px !important;
            height: 150px !important;
            border-radius: 4px !important;
            margin: 0 !important;
            flex-shrink: 0 !important;
            object-fit: contain !important;
            box-sizing: border-box !important;
          }
          
          /* Main image - make it normal flow and ensure it's not cut off */
          .product-page-layout > div:nth-child(2) {
            position: relative !important;
            top: auto !important;
            left: auto !important;
            transform: none !important;
            width: 100% !important;
            height: auto !important;
            min-height: 400px !important;
            margin: 20px 0 !important;
            z-index: 1 !important;
          }
          
          .product-page-layout > div:nth-child(2) img {
            width: 100% !important;
            height: auto !important;
            max-height: 70vh !important;
            min-height: 400px !important;
            border-radius: 4px !important;
            object-fit: contain !important;
          }
          
          /* Product info - make it normal flow */
          .product-page-layout > div:last-child {
            position: relative !important;
            top: auto !important;
            right: auto !important;
            width: 100% !important;
            height: auto !important;
            padding: 0 !important;
            max-width: 100% !important;
            z-index: 1 !important;
          }
        }
        
        @media (max-width: 480px) {
          .product-page-container {
            margin: 10px auto !important;
            padding: 8px 12px !important;
          }
          
          .product-page-layout > div:first-child {
            justify-content: center !important;
          }
          
          .product-page-layout > div:first-child > div:first-child {
            width: auto !important;
            gap: 8px !important;
          }
          
          .product-page-layout > div:first-child > div:first-child img {
            width: 130px !important;
            height: 130px !important;
            object-fit: contain !important;
            box-sizing: border-box !important;
          }
          
          .product-page-layout > div:first-child > div:nth-child(2) {
            width: 130px !important;
            height: 130px !important;
            display: flex !important;
            align-items: center !important;
            justify-content: center !important;
          }
          
          .product-page-layout > div:first-child > div:nth-child(2) img {
            width: 130px !important;
            height: 130px !important;
            display: block !important;
            object-fit: contain !important;
            box-sizing: border-box !important;
          }
          
          .product-page-layout > div:first-child > div:last-child {
            width: auto !important;
            gap: 8px !important;
          }
          
          .product-page-layout > div:first-child > div:last-child img {
            width: 130px !important;
            height: 130px !important;
            object-fit: contain !important;
            box-sizing: border-box !important;
          }
          
          .product-page-layout > div:nth-child(2) {
            height: auto !important;
            min-height: 350px !important;
          }
          
          .product-page-layout > div:nth-child(2) img {
            height: auto !important;
            max-height: 65vh !important;
            min-height: 350px !important;
            object-fit: contain !important;
          }
          
          .product-page-layout h2 {
            font-size: 20px !important;
          }
          
          .product-page-layout > div:last-child > div:first-child > div {
            font-size: 16px !important;
          }
        }
        
        /* Hide spacer on mobile since fixed positioning is removed */
        @media (max-width: 768px) {
          .product-page-spacer {
            display: none !important;
          }
        }
        
        /* FINAL OVERRIDE: Ensure left column can scroll - this must be last to override everything */
        .product-page-layout > div:first-child.hide-scrollbar {
          overflow-y: auto !important;
          overflow-x: visible !important;
        }

        /* Right product panel: fixed-height column — inner scroller must have flex-basis 0 or content gets clipped at bottom */
        @media (min-width: 769px) {
          .product-page-layout > div:last-child .product-page-right-panel-scroll {
            flex: 1 1 0% !important;
            min-height: 0 !important;
            max-height: 100% !important;
            overflow-y: auto !important;
            overflow-x: hidden !important;
            padding-bottom: 48px !important;
            box-sizing: border-box !important;
          }
        }
      `}</style>
    </div>
  );
} 